import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchChapterIdBySlug, fetchEpisode } from "../../api/peContent";
import type { Tier } from "../../api/serving";
import type { Lang, PeChapter } from "../../data/peCurriculum";
import useUserStore from "../../stores/user";
import TeacherMaterialsRail from "./pe-serving/TeacherMaterialsRail";

// Roles that may open the teacher materials (same list as /teacher/lesson-preview).
const MATERIAL_ROLES = ["teacher", "admin", "district_admin", "super_admin"];

/**
 * Inline chapter player for the signed-in Curriculum page.
 *
 * Same READ-ONLY resolve as the /present projection route (PresentationRoute):
 * slug → pe_chapters.id → fetchEpisode(id, tier, lang) → vimeo_id. pe_* tables
 * are RLS-gated, so this only ever resolves for a signed-in user. Writes nothing.
 * Tier bar + EN/ES toggle mirror the /present overlay; "Present" opens the
 * chrome-free full-bleed route for the same chapter/tier/lang.
 */

const TIERS: Tier[] = ["T1", "T2", "T3", "T4"];
const LANGS: Lang[] = ["en", "es"];

const COPY = {
  en: {
    tier: "Tier",
    loading: "Loading episode…",
    missing: "No episode for this chapter at",
    present: "Present full screen",
    back: "← Back to chapters",
    chapter: "Chapter",
  },
  es: {
    tier: "Nivel",
    loading: "Cargando episodio…",
    missing: "No hay episodio para este capítulo en",
    present: "Presentar en pantalla completa",
    back: "← Volver a los capítulos",
    chapter: "Capítulo",
  },
};

const CurriculumChapterPlayer: React.FC<{
  chapter: PeChapter;
  initialLang: Lang;
  onBack: () => void;
}> = ({ chapter, initialLang, onBack }) => {
  const [tier, setTier] = useState<Tier>("T1");
  const [lang, setLang] = useState<Lang>(initialLang);
  const [chapterId, setChapterId] = useState<string | null>(null);
  const [vimeoId, setVimeoId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const profile = useUserStore((s) => s.profile);
  const showMaterials = !!profile && MATERIAL_ROLES.includes(profile.role);
  const t = COPY[lang];
  const title = lang === "es" && chapter.title_es ? chapter.title_es : chapter.title;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const id = chapterId ?? (await fetchChapterIdBySlug(chapter.slug));
      if (cancelled) return;
      setChapterId(id);
      const ep = id ? await fetchEpisode(id, tier, lang) : null;
      if (!cancelled) setVimeoId(ep?.vimeoId ?? null);
    })()
      .catch((err) => {
        console.warn("[CurriculumChapterPlayer] episode load failed:", err);
        if (!cancelled) setVimeoId(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // chapterId is a cache of the slug lookup, not an input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.slug, tier, lang]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden mb-10">
      <div className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <button onClick={onBack} className="text-sm font-semibold text-indigo-700 hover:underline">
            {t.back}
          </button>
          <div className="mt-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
              {t.chapter} {chapter.chapter}
            </span>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Tier">
            {TIERS.map((tr) => (
              <button
                key={tr}
                type="button"
                onClick={() => setTier(tr)}
                aria-pressed={tier === tr}
                className={`px-3 py-2 text-sm font-semibold ${
                  tier === tr ? "bg-indigo-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {t.tier} {tr.slice(1)}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Language">
            {LANGS.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                className={`px-3 py-2 text-sm font-semibold uppercase ${
                  lang === l ? "bg-gray-900 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="aspect-video bg-black">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
            {t.loading}
          </div>
        ) : vimeoId ? (
          <iframe
            key={vimeoId}
            src={`https://player.vimeo.com/video/${vimeoId}?autoplay=0&title=0&byline=0&portrait=0`}
            className="w-full h-full"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={title}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 px-6 text-center">
            {t.missing} {tier} · {lang.toUpperCase()}.
          </div>
        )}
      </div>

      {chapterId && (
        <div className="p-4 text-right pe-noprint">
          <Link
            to={`/present/${chapterId}?tier=${tier}&lang=${lang}`}
            className="text-sm font-semibold text-indigo-700 hover:underline"
          >
            {t.present} →
          </Link>
        </div>
      )}

      {chapterId && showMaterials && (
        <TeacherMaterialsRail
          chapterId={chapterId}
          chapterLabel={`${t.chapter} ${chapter.chapter}`}
          tier={tier}
          onTierChange={setTier}
          lang={lang}
        />
      )}
    </div>
  );
};

export default CurriculumChapterPlayer;
